import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import {
    calcReport,
    calcCompareReport,
    deleteVariant,
    fetchVariantsByUser,
    saveVariant,
    setFavoritesForVariant,
    setError as setErrorAction,
    // setVariantWithFetchModeling,
    clearFilters,
    initModelingByVariant,
    fetchReportColumns,
    clearVariant,
    clearParameters,
    clearReport,
} from "../../../reducers/reports-modeling/roleLevelSlice";

import { DIALOG_TYPES } from "../../../utils/sodReports";

import { ProgressDialog, ErrorsDialog } from "../../common/dialogs";
import useReduxValue from "../../common/hooks/useReduxValue";

import { FavoritesDialog, SaveVariantDialog, DeleteVariantDialog, VariantsListBox, VariantsListDialog } from "../../sod-reports/report-variants";

import { ReportHeader } from "../../sod-reports/report-header";

import ReportsModelingActions from "../reports-modeling-actions";

import RoleLevelModelingBox from "./role-level-modeling-box.jsx";
import RoleLevelModelingParameters from "./role-level-modeling-parameters.jsx";
import RoleLevelModelingResults from "./role-level-modeling-results.jsx";
import { REPORT_MODELING_CONFLICT_LEVELS } from "../../../utils/reports-modeling-utils";

const RoleLevelModelingPage = () => {
    const { variantName: encodedVariantName } = useParams();
    const variantName = encodedVariantName ? decodeURIComponent(encodedVariantName) : undefined;

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const currentVariant = useSelector((state) => state.reportsModeling.roleLevel.currentVariant);
    const [error, setError] = useReduxValue((state) => state.reportsModeling.roleLevel.error, setErrorAction);

    const [variantDialogType, setVariantDialogType] = useState(null);
    const [dialogVariant, setDialogVariant] = useState(null);

    const busy = useSelector((state) => state.reportsModeling.roleLevel.busy);
    const busyType = useSelector((state) => state.reportsModeling.roleLevel.busyType);
    const variantCreated = useSelector((state) => state.reportsModeling.roleLevel.variantCreated);
    const variants = useSelector((state) => state.reportsModeling.roleLevel.variants);
    const parametersOpen = useSelector((state) => state.reportsModeling.roleLevel.parametersOpen);
    const validationErrors = useSelector((state) => state.reportsModeling.roleLevel.validationErrors);

    const systemsExtended = useSelector((state) => state.reportsModeling.roleLevel.parameters.systems);

    const roles = useSelector((state) => state.reportsModeling.roleLevel.parameters.roles);

    const isAnalyzeDisabled = !(roles?.length && systemsExtended.length === 1);

    useEffect(() => {
        dispatch(fetchVariantsByUser());

        return () => {
            dispatch(clearFilters());
            dispatch(clearVariant());
            dispatch(clearParameters());
            dispatch(clearReport());
        };
    }, [dispatch]);

    useEffect(() => {
        if (currentVariant && !variantName) {
            dispatch(clearVariant());
            dispatch(clearParameters());
            dispatch(clearReport());
            return;
        }

        if (!currentVariant && !variantName) {
            return;
        }

        if (variantName && currentVariant?.name === variantName) {
            return;
        }

        if (variantName) {
            const newCurrentVariant = variants.find((variant) => variant.name === variantName);

            if (!newCurrentVariant) {
                return;
            }

            dispatch(initModelingByVariant({ variant: newCurrentVariant }));
        }
    }, [variantName, variants, currentVariant, dispatch]);

    useEffect(() => {
        if (variantCreated) {
            hideVariantDialog();
        }
    }, [variantCreated]);

    const hideVariantDialog = () => {
        setDialogVariant(null);
        setVariantDialogType(null);
    };

    // Variants list dialog
    const handleShowVariantsClick = () => {
        setVariantDialogType(DIALOG_TYPES.VARIANTS);
    };

    // Delete dialog
    const handleDeleteDialogConfirmClick = () => {
        dispatch(deleteVariant(dialogVariant.name));
        hideVariantDialog();
    };

    const handleDeleteClick = (variant) => {
        setFavoritesForVariant({
            variant: dialogVariant,
            inFavorites: false,
        });
        setVariantDialogType(DIALOG_TYPES.DELETE);
        setDialogVariant(variant);
    };

    // Favorites dialog
    const handleFavoritesDialogConfirmClick = () => {
        dispatch(
            setFavoritesForVariant({
                variant: dialogVariant,
                inFavorites: !dialogVariant.inFavorites,
            })
        );

        hideVariantDialog();
    };

    const handleFavoriteClick = (variant) => {
        setVariantDialogType(DIALOG_TYPES.FAVORITES);
        setDialogVariant(variant);
    };

    // Save dialog
    const handleSaveDialogConfirmClick = async (variantName) => {
        await dispatch(saveVariant({ variantName })).unwrap();

        hideVariantDialog();

        navigate(`/modeling-role-level/${encodeURIComponent(variantName)}`);
    };

    const handleSaveClick = () => {
        setVariantDialogType(DIALOG_TYPES.SAVE);
    };

    const handleSubmitClick = async () => {
        await dispatch(calcReport({ conflictLevel: REPORT_MODELING_CONFLICT_LEVELS.ROLE, calcWithReset: true })).unwrap();
        await dispatch(fetchReportColumns());
    };

    const handleSearchVariant = useCallback(
        (event) => {
            dispatch(fetchVariantsByUser(event.target.value));
        },
        [dispatch]
    );

    const handleVariantClick = (variant) => {
        if (!variant) return;

        if (variant.name !== variantName) {
            const encodedVariant = encodeURIComponent(variant.name);

            if (variantName) {
                navigate(`../${encodedVariant}`, { relative: "path" });
            } else {
                navigate(encodedVariant);
            }
        }
    };

    const handleCompareReportsAction = () => {
        dispatch(calcCompareReport());
    };

    const variantsListDialogOpen = variantDialogType === DIALOG_TYPES.VARIANTS;
    const saveDialogOpen = variantDialogType === DIALOG_TYPES.SAVE;
    const deleteDialogOpen = dialogVariant && variantDialogType === DIALOG_TYPES.DELETE;
    const favoritesDialogOpen = dialogVariant && variantDialogType === DIALOG_TYPES.FAVORITES;

    return (
        <>
            <ReportHeader
                parameters={<RoleLevelModelingParameters onShowVariantsClick={handleShowVariantsClick} />}
                variants={
                    <VariantsListBox
                        variants={variants}
                        onDeleteClick={handleDeleteClick}
                        onFavoriteClick={handleFavoriteClick}
                        onItemClick={handleVariantClick}
                        onSearch={handleSearchVariant}
                    />
                }
                parametersOpen={parametersOpen}
            />

            <RoleLevelModelingBox />

            <ReportsModelingActions
                onSubmitClick={handleSubmitClick}
                onSaveClick={handleSaveClick}
                isAnalyzeDisabled={isAnalyzeDisabled}
                onReportCompare={handleCompareReportsAction}
            />

            <RoleLevelModelingResults />

            {saveDialogOpen && (
                <SaveVariantDialog
                    defaultName={currentVariant?.name}
                    open={saveDialogOpen}
                    validationErrors={validationErrors}
                    onSaveClick={handleSaveDialogConfirmClick}
                    onCancelClick={hideVariantDialog}
                />
            )}

            {deleteDialogOpen && (
                <DeleteVariantDialog
                    open={deleteDialogOpen}
                    variant={dialogVariant}
                    onDeleteClick={handleDeleteDialogConfirmClick}
                    onCancelClick={hideVariantDialog}
                />
            )}

            {favoritesDialogOpen && (
                <FavoritesDialog
                    open={favoritesDialogOpen}
                    variant={dialogVariant}
                    onFavoritesClick={handleFavoritesDialogConfirmClick}
                    onCancelClick={hideVariantDialog}
                />
            )}

            {variantsListDialogOpen && (
                <VariantsListDialog
                    open={variantsListDialogOpen}
                    variants={variants}
                    onDeleteClick={handleDeleteClick}
                    onFavoriteClick={handleFavoriteClick}
                    onItemClick={handleVariantClick}
                    onSearch={handleSearchVariant}
                    onClose={hideVariantDialog}
                />
            )}

            <ProgressDialog open={busy} busyType={busyType} />

            <ErrorsDialog error={error} open={error?.type === "error"} onClose={() => setError(null)} />
        </>
    );
};

export default RoleLevelModelingPage;
